/**
 * E08-F01-T06 — Outlook Connect / Disconnect UI + status indicator.
 *
 * 1:1 port of the legacy `dashboard.js` connectOutlook / disconnectOutlook +
 * the sidebar Outlook widget. Two surfaces share this ONE component:
 *   - the sidebar of the CURRENT user — Connected (with outlook_email + a
 *     Disconnect button) OR a "Connect Outlook" button;
 *   - the Admin → Users row of ANOTHER user — the same Connected/Connect badge.
 *
 * Connect flow (legacy connectOutlook): fetch the authorize URL, open it in a
 * popup, and listen for the popup's `postMessage`:
 *   - `oauth-success` → refresh status (invalidate) + success toast;
 *   - `oauth-error`   → error toast with the message;
 *   - a blocked popup (`window.open` returns null) → "Popup blocked" toast.
 *
 * Disconnect (legacy disconnectOutlook): confirm, then POST /outlook/disconnect
 * (own id omitted; an admin passes the target userId). The status comes from a
 * prop (so the admin row can pass get_users' has_outlook) OR, when omitted, from
 * the session `useOutlookStatus` query (the sidebar's own account).
 *
 * Security: the popup message is accepted ONLY from this window's origin or the
 * API's own origin (the callback page is served BY the API — on this deploy that
 * is a different host, `api.gap.eer.email` vs `gap.eer.email`), and only the
 * `type` discriminant is read (never injected as markup) — XSS-safe.
 *
 * The popup can also go away WITHOUT any message — the user closes it, or
 * Microsoft refuses before ever redirecting back (e.g. AADSTS50011 when the
 * redirect URI is not registered). A watchdog therefore polls `popup.closed`,
 * clears the "Connecting…" state and re-reads the status, so the button can
 * never stay disabled forever. Cross-Origin-Opener-Policy may hide `closed`
 * from us while the popup sits on the Microsoft origin (Chrome logs
 * "COOP policy would block the window.closed call" and can report `false`
 * forever), so the same watchdog also gives up after a hard deadline.
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { Dialog } from '../ui/Dialog.js';
import { useToast } from '../ui/ToastContext.js';
import { API_BASE_URL } from '../api/client.js';
import { acceptedMessageOrigins } from './oauthOrigins.js';
import { fetchAuthorizeUrl, useOutlookStatus, useDisconnectOutlook } from './outlookApi.js';

/** How often the watchdog checks whether the OAuth popup is gone (ms). */
const POPUP_POLL_MS = 1200;

/**
 * Hard deadline for one connect attempt (ms). Reached only when the popup never
 * reports back AND `popup.closed` stays unreadable/false (COOP) — the button is
 * released anyway rather than staying disabled for the rest of the session.
 */
const CONNECT_DEADLINE_MS = 180_000;

export interface OutlookConnectProps {
  /**
   * The user whose mailbox this manages. Omit for the CURRENT user (the sidebar
   * widget — status then comes from the session `useOutlookStatus` query).
   */
  readonly userId?: string;
  /** Connected state when managing ANOTHER user (admin row): has_outlook from get_users. */
  readonly hasOutlook?: boolean;
  /** Connected email when managing another user (admin row). */
  readonly outlookEmail?: string | null;
  /** Visual density: `'sidebar'` (stacked) or `'inline'` (admin row badge). */
  readonly variant?: 'sidebar' | 'inline';
  /** Test id prefix (so multiple instances on one page stay addressable). */
  readonly testId?: string;
}

/** The popup message shape the OAuth callback page postMessages back. */
interface OAuthMessage {
  readonly type: 'oauth-success' | 'oauth-error' | 'oauth-admin-consent';
  readonly outlook_email?: string;
  readonly error?: string;
}

export function OutlookConnect({
  userId,
  hasOutlook,
  outlookEmail,
  variant = 'inline',
  testId = 'outlook',
}: OutlookConnectProps): JSX.Element {
  const toast = useToast();
  // Own account → the session status query; another account → the props.
  const isSelf = userId === undefined;
  const statusQuery = useOutlookStatus(isSelf);
  const disconnect = useDisconnectOutlook();
  const [connecting, setConnecting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const popupRef = useRef<Window | null>(null);
  /** Did the popup report back? Keeps the watchdog from double-reporting. */
  const messageSeenRef = useRef(false);

  const connected = isSelf ? Boolean(statusQuery.data?.has_outlook) : Boolean(hasOutlook);
  const email = isSelf ? (statusQuery.data?.outlook_email ?? null) : (outlookEmail ?? null);

  // Listen for the popup's postMessage (success/error). Only this origin and the
  // API origin (which serves the callback page) are accepted; only the `type`
  // discriminant drives the reaction.
  useEffect(() => {
    const allowed = acceptedMessageOrigins(window.location, API_BASE_URL);
    function onMessage(event: MessageEvent): void {
      if (!allowed.includes(event.origin)) return;
      const data = event.data as OAuthMessage | undefined;
      if (!data || typeof data.type !== 'string') return;
      messageSeenRef.current = true;
      if (data.type === 'oauth-success') {
        setConnecting(false);
        toast.success('Outlook connected');
        void statusQuery.refetch();
      } else if (data.type === 'oauth-error') {
        setConnecting(false);
        toast.error(data.error || 'Outlook connection failed');
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watchdog: the popup may vanish without a message (user closed it, or
  // Microsoft rejected the request before redirecting back — e.g. an
  // unregistered redirect URI). Re-read the status and release the button so it
  // can never stay stuck on "Connecting…".
  useEffect(() => {
    if (!connecting) return;
    const deadline = Date.now() + CONNECT_DEADLINE_MS;
    const timer = window.setInterval(() => {
      const popup = popupRef.current;
      // Not opened yet → keep waiting; `onConnect` clears `connecting` itself
      // when the popup could not be opened at all.
      const gone = Boolean(popup?.closed);
      if (!gone && Date.now() < deadline) return;
      window.clearInterval(timer);
      popupRef.current = null;
      setConnecting(false);
      if (!isSelf) return;
      void statusQuery.refetch().then((result) => {
        // No message arrived and the mailbox is still not connected — say so,
        // instead of leaving the user with a silently unchanged widget.
        if (!messageSeenRef.current && !result.data?.has_outlook) {
          toast.error('Outlook was not connected — the sign-in window closed before finishing');
        }
      });
    }, POPUP_POLL_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connecting]);

  const onConnect = useCallback(async (): Promise<void> => {
    setConnecting(true);
    messageSeenRef.current = false;
    try {
      const { authorize_url } = await fetchAuthorizeUrl();
      const popup = window.open(authorize_url, 'outlook-oauth', 'width=600,height=720');
      if (!popup) {
        // A blocked popup → notify (acceptance #3).
        setConnecting(false);
        toast.error('Popup blocked — allow popups for this site and try again');
        return;
      }
      popupRef.current = popup;
    } catch {
      setConnecting(false);
      toast.error('Could not start the Outlook connection');
    }
  }, [toast]);

  function onDisconnect(): void {
    disconnect.mutate(userId, {
      onSuccess: () => {
        toast.success('Outlook disconnected');
        if (isSelf) void statusQuery.refetch();
      },
      onError: (error) => toast.error(error.detail.message || 'Disconnect failed'),
      onSettled: () => setConfirmOpen(false),
    });
  }

  const stacked = variant === 'sidebar';

  return (
    <div
      data-testid={`${testId}-widget`}
      data-connected={connected ? 'true' : 'false'}
      className={stacked ? 'flex flex-col gap-1' : 'inline-flex items-center gap-2'}
    >
      {connected ? (
        <>
          <span
            data-testid={`${testId}-connected`}
            className="inline-flex items-center gap-1 text-xs font-medium text-green-600"
            title={email ? `Connected: ${email}` : 'Connected'}
          >
            <i className="fa-solid fa-circle-check" aria-hidden />
            Connected
            {email ? (
              <span data-testid={`${testId}-email`} className="ml-1 text-[var(--color-text-muted)]">
                {email}
              </span>
            ) : null}
          </span>
          <button
            type="button"
            data-testid={`${testId}-disconnect`}
            onClick={() => setConfirmOpen(true)}
            className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
          >
            Disconnect
          </button>
        </>
      ) : (
        <button
          type="button"
          data-testid={`${testId}-connect`}
          onClick={() => void onConnect()}
          disabled={connecting}
          className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] bg-white px-2.5 py-1 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-surface-muted)] disabled:opacity-60"
        >
          <i className="fa-brands fa-microsoft" aria-hidden />
          {connecting ? 'Connecting…' : 'Connect Outlook'}
        </button>
      )}

      {confirmOpen ? (
        <Dialog
          open
          onClose={() => setConfirmOpen(false)}
          title="Disconnect Outlook?"
          testId={`${testId}-confirm`}
          maxWidth="sm"
          footer={
            <>
              <button
                type="button"
                data-testid={`${testId}-confirm-cancel`}
                onClick={() => setConfirmOpen(false)}
                className="rounded border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-medium hover:bg-[var(--color-surface-muted)]"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid={`${testId}-confirm-ok`}
                onClick={onDisconnect}
                disabled={disconnect.isPending}
                className="rounded bg-[var(--color-danger)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
              >
                Disconnect
              </button>
            </>
          }
        >
          <p className="text-sm text-[var(--color-text)]">
            This removes the Microsoft mailbox connection{email ? ` for ${email}` : ''}. You can
            reconnect at any time.
          </p>
        </Dialog>
      ) : null}
    </div>
  );
}

export default OutlookConnect;
